import Copy16 from '@carbon/icons-react/es/copy/16';
import Edit16 from '@carbon/icons-react/es/edit/16';
import Movement16 from '@carbon/icons-react/es/movement/16';
import OverflowMenuVertical16 from '@carbon/icons-react/es/overflow-menu--vertical/16';
import { Dropdown, Tooltip } from 'antd';
import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { ProjectId } from 'api/models';
import { SpaceTab } from 'api/space';
import { Button } from 'components/atoms/button';
import { Menu } from 'components/atoms/dropdown';
import { EmbedSpaceTabEditModal } from 'components/molecules/space-embed-edit-modal';
import { PermissionRuleName } from 'constant';
import { SpaceTab as SpaceTabT } from 'data/space-tabs';
import { usePermissionForRule } from 'hooks/permissions/use-permission';
import { useTranslation } from 'hooks/use-translation';
import { RearrangeDocs } from '../rearrange/modal';
import { useTabMoreActions } from './use-tab-more-actions';

enum MoreActionsKeys {
  Copy = 'Copy',
  Edit = 'Edit',
  Rearrange = 'Rearrange',
  Delete = 'Delete',
}

type SpacesTabMoreActionsTypes = {
  spaceTab: SpaceTab | SpaceTabT;
  projectId: number;
  showEdit?: boolean;
  privateSpace?: boolean;
};

export const SpaceTabMoreActions = ({
  spaceTab,
  projectId,
  showEdit = true,
  privateSpace,
}: SpacesTabMoreActionsTypes) => {
  const moreActions = useTranslation('more_actions');
  const { spaceTabTitle, url } = spaceTab;
  const [showRearrangeModal, setRearrangeModalVisibility] = useState(false);
  const onRearrange = useCallback(() => {
    setRearrangeModalVisibility(true);
  }, []);
  const onClose = useCallback((e: Event) => {
    e.stopPropagation();
    setRearrangeModalVisibility(false);
  }, []);

  const {
    isModalVisible,
    isLoading,
    setTrue,
    setFalse,
    handleCopy,
    deleteConfirmation,
    handleOnFinish,
  } = useTabMoreActions({
    spaceTab,
    projectId,
  });

  const { isAllowedForCurrentUser } = usePermissionForRule();

  const TabActionsMenu = () => (
    <Menu>
      <Menu.Header>
        <FormattedMessage id="more_actions" />
      </Menu.Header>
      <Menu.Item
        key={MoreActionsKeys.Copy}
        icon={<Copy16 />}
        onClick={handleCopy}
      >
        <FormattedMessage id="space_tab.copy_url" />
      </Menu.Item>
      {showEdit && (
        <Menu.Item
          key={MoreActionsKeys.Edit}
          onClick={setTrue}
          icon={<Edit16 />}
        >
          <FormattedMessage id="edit" />
        </Menu.Item>
      )}
      {(
        privateSpace
          ? true
          : isAllowedForCurrentUser(
              PermissionRuleName.SpaceSharedDocumentRearrangeAction,
            )
      ) ? (
        <Menu.Item
          key={MoreActionsKeys.Rearrange}
          icon={<Movement16 />}
          onClick={onRearrange}
        >
          <FormattedMessage id="space_tab.rearrange" />
        </Menu.Item>
      ) : null}
      {(
        privateSpace
          ? true
          : isAllowedForCurrentUser(
              PermissionRuleName.SpaceSharedDocumentDeleteAction,
            )
      ) ? (
        <>
          <Menu.Divider />
          <Menu.ItemDelete
            key={MoreActionsKeys.Delete}
            onClick={deleteConfirmation}
          />
        </>
      ) : null}
    </Menu>
  );

  return (
    <>
      <Tooltip title={moreActions} placement="topRight">
        <Dropdown
          className="more-actions"
          overlay={TabActionsMenu}
          trigger={['click']}
          placement="bottomLeft"
        >
          <Button type="text" icon={<OverflowMenuVertical16 />} size="small" />
        </Dropdown>
      </Tooltip>
      <EmbedSpaceTabEditModal
        initialValues={{
          url,
          spaceTabTitle,
        }}
        onCancel={setFalse}
        isModalVisible={isModalVisible}
        isLoading={isLoading}
        onFinish={handleOnFinish}
      />
      <RearrangeDocs
        visible={showRearrangeModal}
        onClose={onClose}
        currentProjectId={projectId as unknown as ProjectId}
      />
    </>
  );
};
